import React, { useEffect, useState } from 'react';
import FadeLoader from "react-spinners/FadeLoader";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { format, isAfter, parse } from 'date-fns';

const HolidayPopup = ({
  open,
  onClose,
  onHolidaySelect,
  selectedCountry,
  currentYear,
}) => {
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const today = new Date();

  const hostPath = process.env.REACT_APP_API_BASE_URL;
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;

  // Reset fetch trigger if year or country changes
  useEffect(() => {
    setHasFetched(false);
  }, [currentYear, selectedCountry]);

  useEffect(() => {
    if (!open || hasFetched) return;

    const fetchHolidays = async () => {
      try {
        setIsLoading(true);       // Show loader
        setHolidays([]);          // Clear previous holidays

        const response = await fetch(`${hostPath}/api/GetAllHolidays`, {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "x-functions-key": `${hostHeader}`
          },
          body: JSON.stringify({
            Region: selectedCountry,
            Year: currentYear
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const filtered = data
          .map((h) => {
            const dateObj = new Date(h.date); // Use ISO string instead of broken year/month/day
            return { ...h, dateObj };
          })
          .filter((h) => {
            const isActive = h.isActive;
            const regionMatch =
              selectedCountry?.toLowerCase().trim() === h.region?.toLowerCase().trim();
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize
            const isFuture = h.dateObj > today;

            return isActive && regionMatch && isFuture;
          });

        setHolidays(filtered);
        setHasFetched(true);
      } catch (error) {
        console.error('HolidayPopup: Error fetching holidays:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHolidays();
  }, [open, hasFetched, hostPath, hostHeader, selectedCountry, currentYear]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        Upcoming {selectedCountry} Holidays
      </DialogTitle>

      <DialogContent dividers style={{ minHeight: '200px' }}>
        {isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px'
          }}>
            <FadeLoader color="#36d7b7" loading={isLoading} />
          </div>
        ) : holidays.length === 0 ? (
          <Typography variant="body1">No upcoming holidays found!</Typography>
        ) : (
          holidays.map((holiday) => {
            return (
              <div
                key={holiday.id || holiday.name}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <Typography variant="body1">
                  <strong>{holiday.name}</strong> — {holiday.dateName}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const parsedDate = parse(holiday.dateName, 'MMMM d, yyyy', new Date());
                    const holidayDateStr = format(parsedDate, 'yyyy-MM-dd');
                    onHolidaySelect({
                      name: holiday.name,
                      date: holidayDateStr,
                    });
                    onClose();
                  }}
                >
                  Schedule
                </Button>
              </div>
            );
          })
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HolidayPopup;